import { Box } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import axios, { AxiosError } from 'axios';
import i18n from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import dcgaryLogo from "../../assets/images/dc-gary-logo.png";
import rcboLogo from "../../assets/images/rcbo-logo.png";
import * as constants from "../../constants";
import environment from '../../environments/environment';
import { resetAllRole, setAllRoleDetails } from '../../store/features/auth/allRoleDetailsSlice';
import { setLoginDetails } from '../../store/features/auth/authSlice';
import { setParticipantDetails } from '../../store/features/auth/fundParticipantsSlice';
import { setParticipantAdminProfile } from '../../store/features/auth/participantAdminProfileSlice';
import { fetchThemeDetailsByClientId } from '../../store/features/themeSlice';
import { setMenuItems } from '../../store/MenutItems/menuItemsSlice';
import { setActiveIndex, setOpenMenuIndex } from '../../store/MenutItems/menuUiSlice';
import { AppDispatch, persistor, RootState } from '../../store/store';
import { saveAuditlog } from '../fiscalUpgrade/services/AuditLogs/AuditLogsServices';
import { getMenuItemsByClientId } from '../fiscalUpgrade/services/MenuItemsServices';
import { getAllParticipants } from '../fiscalUpgrade/services/ParticipantsServices/SwitchParticipartService';
import { getAssetSummaryByPId } from '../fiscalUpgrade/services/Summary/accountSummaryService';
import { getAllRoleDetails, getUserDetailsByMailId } from '../fiscalUpgrade/services/Users/user-Info.service';
import { getClientDetailsByClientName } from '../fiscalUpgrade/Utils/ClientSettingsTable';
import { getCurrentPSTTime } from '../fiscalUpgrade/Utils/dayjsWithTZ';
import { notifyUser } from '../fiscalUpgrade/Utils/notifyUser';
import AccessDeniedMessage from './AccessDeniedMessage';

const theme = createTheme();
interface CustomizeHeaderProps {
  setView: React.Dispatch<React.SetStateAction<string>>;
}

const langMap: Record<string, string> = {
  English: 'en',
  Spanish: 'es',
  en: 'en',
  es: 'es'
};

export default function SignIn({ setView }: CustomizeHeaderProps) {
  const popupRef = useRef<Window | null>(null);
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const [errorValidationMessage, setErrorValidationMessage] = useState('');
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const userInfo = useSelector((state: RootState) => state.auth);
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const allowedRolesForSettings = [constants.ROLES_ADMIN_MANAGER, constants.ROLES_BUDGET_ANALYST,
  constants.ROLES_ADMIN_USER, constants.ROLES_FINANCIAL_ANALYST];
  const onSignIn = async () => {
    sessionStorage.setItem("firstTime", "valid");
    sessionStorage.setItem("sessionExpired", "false");
    window.location.href = `https://login.microsoftonline.com/${environment.tenant}/oauth2/authorize?client_id=${environment.clientId}&response_type=code&redirect_uri=${environment.ApplicationURL}&response_mode=query&scope=${environment.scope}&state=1234567890`;
  };

  // Prevent navigating back from SignIn
  useEffect(() => {
    window.history.pushState(null, document.title, window.location.href);
    const handleBackButton = () => window.history.pushState(null, document.title, window.location.href);
    window.addEventListener('popstate', handleBackButton);
    return () => window.removeEventListener('popstate', handleBackButton);
  }, []);

  // Handle OAuth redirect
  useEffect(() => {
    let authorityCode: string | undefined;
    let BlackBaudcode: string | null = null;
    if (window.location.href.includes("code")) {
      dispatch({ type: 'RESET' });
      const url = window.location.href;
      const params = new URLSearchParams(new URL(url).search);
      const codeParam = params.get("code");
      const origin = window.location.origin;
      const pathname = window.location.pathname.replace(/\/$/, "");
      const isBlackBaudFlow = params.get("source") === "blackbaud";
      const isLocalFiscal =
        origin === "http://localhost:3000" && pathname === "/fiscal";
      if (codeParam && !isBlackBaudFlow) {
        authorityCode = codeParam;
      }
      dispatch(
        setLoginDetails({
          authorityValue: authorityCode,
        })
      );
      // If this is a BlackBaud popup flow, return the code to opener and close early
      if (isBlackBaudFlow) {
        try {
          if (isBlackBaudFlow && window.opener && !window.opener.closed) {
            window.opener.postMessage(
              { type: "BLACKBAUD_AUTH_SUCCESS", code: codeParam },
              window.location.origin
            );
          }
        } catch (err) {
          console.error("Error dispatching Blackbaud code to opener:", err);
        } finally {
          try {
            window.close();
          } catch (e) {
            /* ignore */
          }
          return; // stop further AAD handling for BlackBaud popup flow
        }
      } else if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          { type: "BLACKBAUD_AUTH_FAILED", code: " " },
          window.location.origin
        );
        window.close();
      }
      axios
        .get(
          `${environment.aadSecureAuthority}${authorityCode}&redirectUrl=${environment.ApplicationURL}&app=FISCAL`
        )
        .then((response) => {
          if (response.status === constants.STATUS_CODES.SUCCESS) {
            getAccessTockenDetails(response.data.data);
          }
        })
        .catch((error) => {
          console.error("Error:", error);
          sessionStorage.setItem("firstTime", "inValid");
          setErrorValidationMessage(constants.AUTH_SERVICES_VALIDATION);
          setIsAccessDenied(true);
          dispatch({ type: "RESET" }); // Will reset all state to initial values
          persistor.purge();
        });

    } else if (!userInfo.isLogin && userInfo.expiresOn && userInfo.messageRes) {
      dispatch(setLoginDetails({ ...userInfo, isLogin: false }));
      setErrorValidationMessage(constants.USER_VALIDATION_USER_DOSE_NOT_HAVE_ACCESS_TO_FISCIAL_APP);
      sessionStorage.setItem("firstTime", "inValid");
      sessionStorage.setItem("accessDenied", "valid");
      sessionStorage.setItem("errorMsg", constants.USER_VALIDATION_USER_DOSE_NOT_HAVE_ACCESS_TO_FISCIAL_APP);
    } else {
      if (
        (!sessionStorage.getItem("firstTime") || sessionStorage.getItem("firstTime") === "valid")
      ) {
        dispatch({ type: 'RESET' });
        onSignIn();
      }
    }
  }, []);

  function getAccessTockenDetails(loginDetails: any) {
    const userInfo = {
      isLogin: true,
      userName: `${loginDetails.userInfo.name}`,
      firstName: loginDetails.userInfo.givenName,
      lastName: loginDetails.userInfo.familyName,
      emailId: loginDetails.userInfo.displayableId,
      // language: loginDetails.userInfo.language,
      jwtToken: loginDetails.accessToken,
      refreshToken: loginDetails.refreshToken,
      expiresIn: loginDetails.expiresIn,
      expiresOn: loginDetails.expiresOn,
      homeAccountId: loginDetails.userInfo.homeAccountId,
      tenantId: loginDetails.userInfo.displayableId.split("@")[1],
    };
    dispatch(setLoginDetails(userInfo));
    i18n.changeLanguage(langMap[loginDetails.userInfo.language] || 'en');

    if (loginDetails.accessToken) {
      getDetailsByMailId(loginDetails.userInfo.displayableId, loginDetails);
    } else {
      dispatch(setLoginDetails({ ...userInfo, isLogin: false }));
      sessionStorage.setItem("accessDenied", "valid");
      sessionStorage.setItem("firstTime", "inValid");
      sessionStorage.setItem("errorMsg", constants.USER_VALIDATION_USER_DOSE_NOT_HAVE_ACCESS_TO_FISCIAL_APP);
    }
  }
  function getRedirectPathByRole(role: string, redirectPath: string | null, isValidPath: boolean, userInfoParam?: any): string {

    const summaryRoles = [
      constants.ROLES_ADMIN_MANAGER,
      constants.ROLES_ADMIN_USER,
      constants.ROLES_PARTICIPANT_USER,
      constants.ROLES_READ_ONLY_USER,
      constants.ROLES_READ_ONLY_ALL_LOCATIONS,
      constants.ROLES_FINANCIAL_ANALYST,
      constants.ROLES_PROJECT_APPROVER
    ];

    const projectInvoiceRoles = [
      constants.ROLES_PROJECT_MANAGER
    ];

    const invoiceTaskRoles = [
      constants.ROLES_BUDGET_ANALYST,
      constants.ROLES_PROJECT_ANALYST
    ];

    // If session redirect is available, prefer that.
    if (redirectPath && isValidPath) {
      sessionStorage.removeItem("redirectAfterLogin");
      return redirectPath;
    }

    // Otherwise, pick a default route by role

    const info = userInfoParam || userInfo;
    const ClientDetails = getClientDetailsByClientName(info?.clientVo?.name);
    if (ClientDetails?.name === constants.CLIENT_NAME_DC_GARY && role === constants.ROLES_ADMIN_MANAGER) {
      dispatch(
        setParticipantAdminProfile({
          participantAdminProfile: constants.ADMIN_VIEW,
        })
      );
      return "/userListing";
    } else if (summaryRoles.includes(role)) {
      return "/summary";
    } else if (invoiceTaskRoles.includes(role)) {
      if (ClientDetails?.name === constants.CLIENT_NAME_DC_GARY && role === constants.ROLES_BUDGET_ANALYST) {
        dispatch(setActiveIndex(2));
        return "/invoicesPaymentsTransactions";
      } else {
        return "/invoiceTasksSummary";
      }
    } else if (projectInvoiceRoles.includes(role)) {
      if (ClientDetails?.name === constants.CLIENT_NAME_DC_GARY ) {
        return "/invoicesPaymentsTransactions";
      } else {
        return "/vendorInvoiceSummary";
      }
    }
    // Optionally, fallback to a generic page
    return "/summary";
  }

  async function getDetailsByMailId(email: string, loginInfo: any) {
    try {
      const usersDetails = await getUserDetailsByMailId(email);
      if (!usersDetails?.data ||
        usersDetails?.data?.length === 0 ||
        !usersDetails?.data[0]?.roles) {
        dispatch(setLoginDetails({ ...loginInfo, isLogin: false, messageRes: usersDetails.message }));
        sessionStorage.setItem("accessDenied", "valid");
        sessionStorage.setItem("firstTime", "inValid");
        sessionStorage.setItem("errorMsg", constants.USER_VALIDATION_USER_DOSE_NOT_HAVE_ACCESS_TO_FISCIAL_APP);
        navigate(environment.ApplicationBaseName);
        console.error("Error while fetching user info by email id:");

      } else {
        const userLoginDetails = usersDetails.data[0];
        const userInfo = {
          userParticipantXref: userLoginDetails.userParticipantXref,
          clientVo: userLoginDetails.clientVO,
          roles: userLoginDetails.roles,
          aadReferenceId: userLoginDetails.aadReferenceId,
          clientId: userLoginDetails.clientId,
          defaultParticipantId: userLoginDetails.defaultParticipantId,
          id: userLoginDetails.userId,
          language: userLoginDetails.language,
          menuPosition: userLoginDetails.menuPosition,
        };

        let participantVO = {
          page: -1,
          size: -1,
          isActive: constants.IS_ACTIVE_YES,
          clientId: userLoginDetails.clientId
        };
        try {
          const promise = await getAllParticipants(participantVO);
          const participantsData = promise.data?.data?.data || [];
          if (userInfo?.defaultParticipantId && userLoginDetails.clientId && participantsData.length > 0) {
            getAstByPid(userLoginDetails.defaultParticipantId, userLoginDetails.clientId);
          }
          if (userLoginDetails.roles.roleName === constants.ROLES_PROJECT_MANAGER) {
            dispatch(
              setParticipantAdminProfile({
                participantAdminProfile: constants.ADMIN_VIEW,
              })
            );
            dispatch(setParticipantDetails({
              participantsArray: participantsData.length > 0 ? participantsData : null,
              participantId: 0
            }));
          }
          else {
            dispatch(setParticipantDetails({
              participantsArray: participantsData.length > 0 ? participantsData : null,
              participantId: userLoginDetails.defaultParticipantId
            }));
          }
        } catch (error) {
          console.error(error);
          //dispatch(setParticipantDetails({ participantId: userLoginDetails.defaultParticipantId }));
        }
        dispatch(setLoginDetails(userInfo));
        i18n.changeLanguage(langMap[userLoginDetails.language] || 'en');

        const redirectPath = sessionStorage.getItem("redirectAfterLogin");
        getAllRoles(userLoginDetails.clientId);
        const isValidPath = await getMenuItems(userLoginDetails.clientId, userLoginDetails.roles.roleName, redirectPath);
        
       setView( userInfo.menuPosition); 
        const loginDetails = {
          auditLogId: 0,
          dateChanged: getCurrentPSTTime(),
          clientId: userLoginDetails.clientId,
          participantId: userLoginDetails.defaultParticipantId,
          user: `${userLoginDetails.firstName} ${userLoginDetails.lastName}`,
          event: "Login",
          description: `${userLoginDetails.firstName} has logged in.`,
          amount: 0.0,
          version: 0
        }
        saveAuditlog(loginDetails)
        dispatch(setActiveIndex(0));
        dispatch(setOpenMenuIndex(null));
        const role = userLoginDetails.roles.roleName;
        const finalRedirectPath = getRedirectPathByRole(role, redirectPath, isValidPath, userInfo);
        navigate(finalRedirectPath);
      }
    } catch (error) {
      dispatch(setLoginDetails({ ...loginInfo, isLogin: false }));
      sessionStorage.setItem("accessDenied", "valid");
      sessionStorage.setItem("firstTime", "inValid");
      sessionStorage.setItem("errorMsg", constants.USER_VALIDATION_USER_DOSE_NOT_HAVE_ACCESS_TO_FISCIAL_APP);
      navigate(environment.ApplicationBaseName);
    }
  }
  async function getAllRoles(clId: number) {
    try {
      const allRolesRes = await getAllRoleDetails(clId);
      if (allRolesRes?.status === constants.STATUS_CODES.SUCCESS) {
        dispatch(setAllRoleDetails({ roleDetails: allRolesRes?.data?.data }));
      } else {
        dispatch(resetAllRole());
      }
    } catch (error) {
      console.error("Error while fetching all roles:", error);
    }
  }

  function hasRoute(items: any[], redirectPath: string | null): boolean {
    if (!redirectPath) return false;
    const basePath = redirectPath.split("?")[0];
    return items.some((item) => {
      if (!item) return false;
      const routeMatch = item.route === basePath;
      let subMatch = false;
      if (item.subItems && item.subItems.length > 0) {
        subMatch = item.subItems.some((subItem: any) => {
          const subRoute = subItem.route;
          if (!subRoute) return false;
          const subRouteBase = subRoute.split(/[A-Z]/)[0];
          const redirectBase = basePath.split(/[A-Z]/)[0];
          return (
            subItem.route === basePath ||
            subRouteBase === redirectBase ||
            basePath.startsWith(subRouteBase) ||
            subRoute.startsWith(redirectBase)
          );
        });
        if (!subMatch) {
          subMatch = hasRoute(item.subItems, basePath);
        }
      }
      return routeMatch || subMatch;
    });
  }


  async function getMenuItems(clientId: number, roleName: string, redirectPath: string | null): Promise<any> {
    try {
      const menuItemsRes = await getMenuItemsByClientId(clientId);
      if (menuItemsRes.status === constants.STATUS_CODES.SUCCESS) {
        let items = menuItemsRes.data?.data ?? [];
        items = filterMenuItemsByRole(items, roleName);
        const isValidPath = hasRoute(items, redirectPath);
        dispatch(setMenuItems(items));
        return isValidPath;
      }
    } catch (error) {
      console.error(error);
    }
  }



  function filterMenuItemsByRole(menuItems: any[], roleName: string): any[] {
    return menuItems
      .map(item => {
        const newItem = { ...item };
        if (newItem.subItems?.length > 0) {
          newItem.subItems = filterMenuItemsByRole(newItem.subItems, roleName);
        }
        if (
          newItem.label.toLowerCase() === constants.LABEL_NAME_REPORTS &&
          roleName === constants.ROLES_READ_ONLY_ALL_LOCATIONS &&
          Array.isArray(newItem.subItems)
        ) {
          newItem.subItems = newItem.subItems.map((subItem: any) => {
            if (subItem.label === constants.SUBMENU_NAME_PARTICPANT_SUMMARY) {
              return { ...subItem, adminView: "N" };
            }
            return subItem;
          });
        }
        if (newItem.label.toLowerCase() === constants.LABEL_NAME_SETTINGS && !allowedRolesForSettings.includes(roleName)) {
          return null;
        }
        return newItem;
      })
      .filter(Boolean);
  }
  useEffect(() => {
    if (userInfo.clientId > 0 && environment.generalSettings) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const formattedDate = `${yyyy}-${mm}-${dd}`;

      dispatch(fetchThemeDetailsByClientId({ tDate: formattedDate, clientId: userInfo.clientId }));
    }
  }, [userInfo]) 
  useEffect(() => {
    const denied = sessionStorage.getItem("accessDenied");
    const message = sessionStorage.getItem("errorMsg");
    if (message && denied) {
      setIsAccessDenied(true);
      setErrorValidationMessage(message);
    }
  }, []);
  async function getAstByPid(pId: any, clientId: number) {
    try {
      const resAssetByPid = await getAssetSummaryByPId(pId, clientId);
      if (resAssetByPid.status === constants.STATUS_CODES.SUCCESS) {
        if (resAssetByPid && resAssetByPid.data.data.cashAccounts
          && resAssetByPid.data.data.cashAccounts.availableBalance < 0) {
          const message = `Participant has a checking balance overdraft of $${resAssetByPid.data.data.cashAccounts.availableBalance}`;
          notifyUser(message, 'warning');
        }
      }
    } catch (error: any) {
      if (error instanceof AxiosError) {
        console.error("Axios Error", error.response?.data || error.message);
      } else {
        console.error("Unexpected Error:", error);
      }
    }
  }
  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="xs" sx={{
        overflow: !isAccessDenied ? 'hidden' : 'auto',
        height: '60vh',
      }}>
        <CssBaseline />
        <Box sx={{ marginTop: 20, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {!isAccessDenied ? (
            <CircularProgress />
          ) : (
            errorValidationMessage && (
              <AccessDeniedMessage
                logo={environment.clientName === constants.CLIENT_NAME_RCBO ? rcboLogo : dcgaryLogo}
                errorMessage={errorValidationMessage}
              />
            )
          )}
        </Box>
      </Container>
    </ThemeProvider>
  );
}
